// src/pages/lab/labOrders/LabOrdersPage.tsx
import React, { useEffect, useState, useCallback } from "react";
import { Alert } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import axios from "axios";

// Layout and API
import Layout from "../../../components/Layout";
import { getLabOrders, sendResultsForLabOrder, deleteLabOrder } from "../../../api/labiq/LabOrdersApi";
import { LabOrder, parseTestType } from "../../../types/lab/LabOrderTypes";
import { SampleAnalysis } from "../../../types/lab/SampleTypes";
import { createSample } from "../../../api/labiq/SampleApi";

// Import components
import LabOrderTable from "./components/LabOrderTable";
import LabOrderDrawer from "./components/LabOrderDrawer";
import SampleModal from "../tracking/components/SampleModal";
import { PageContainer, PageToolbar } from "../../../components/shared";

import { loadUserPreferences } from "../../../utils/userPrefs/UserPrefs";

//types
import { Sample, defaultSample } from "../../../types/lab/SampleTypes";

import { useNotifications } from "../../../context/NotificationContext";
import { extractValidationErrors } from "../../../utils/apiError";


const LabOrdersPage: React.FC = () => {
	const [labOrders, setLabOrders] = useState<LabOrder[]>([]);
	const [totalLabOrders, setTotalLabOrders] = useState<number>(0);

	const [currentPage, setCurrentPage] = useState<number>(0);

	const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
	const [selectedLabOrders, setSelectedLabOrders] = useState<number[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const [searchQuery, setSearchQuery] = useState<string>("");
	const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>("");
	const [expandedRows, setExpandedRows] = useState<any>(null);
	const [expandedOrderItems, setExpandedOrderItems] = useState<any>(null);

	// Sample creation states
	const [isCreateSampleModalOpen, setIsCreateSampleModalOpen] = useState<boolean>(false);
	const [newSample, setNewSample] = useState<Sample>(defaultSample);
	const [filterStatus, setFilterStatus] = useState<string | null>(null);

	const ORDERS_PER_PAGE = loadUserPreferences().tableRowsPerPage;

	const { showSuccess, showError } = useNotifications();

	const fetchLabOrderData = useCallback(
		async (status?: string) => {
			setIsLoading(true);
			setError(null);
			try {
				setFilterStatus(status || filterStatus || null);
				const data = await getLabOrders(status || filterStatus || null);
				setLabOrders(data);
				setTotalLabOrders(data.length);
			} catch (err: any) {
				console.error("Error fetching lab orders:", err);
				setError(err.message || "Failed to load lab orders.");
			} finally {
				setIsLoading(false);
			}
		},
		[]
	);

	useEffect(() => {
		fetchLabOrderData();
	}, []);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchQuery(searchQuery);
		}, 500);

		return () => clearTimeout(timer);
	}, [searchQuery]);

	useEffect(() => {
		if (debouncedSearchQuery.trim() && labOrders.length > 0) {

			// Find the matching order in the sorted array (matching table display)
			const orderIndex = labOrders.findIndex((order) => 
				order.order_items?.some((item) => item.sample_id?.toString() === debouncedSearchQuery.trim())
			);

			if (orderIndex !== -1) {
				const matchingOrder = labOrders[orderIndex];
				const pageNumber = Math.floor(orderIndex / ORDERS_PER_PAGE);
				setCurrentPage(pageNumber);
				setExpandedRows({ [matchingOrder.id]: true });
				setError(null);
			} else {
				showError(`Order item with ID "${debouncedSearchQuery}" not found`);
			}
		} else if (!debouncedSearchQuery.trim()) {
			setExpandedRows(null);
		}
	}, [debouncedSearchQuery, ORDERS_PER_PAGE, showError]);

	const handleEditClick = useCallback((orderId: number) => {
		setSelectedLabOrders([orderId]);
		setIsDrawerOpen(true);
	}, []);

	const handleNewLabOrderClick = useCallback(() => {
		setSelectedLabOrders([0]);
		setIsDrawerOpen(true);
	}, []);

	const handleDrawerClose = useCallback(() => {
		fetchLabOrderData();
		setSelectedLabOrders([]);
		setIsDrawerOpen(false);
	}, []);

	const handleRefreshLabOrders = useCallback(() => {
		fetchLabOrderData();
	}, []);

	const handleDeleteOrders = useCallback(async () => {
		if (selectedLabOrders.length === 0) return;
		try {
			for (const orderId of selectedLabOrders) {
				await deleteLabOrder(orderId);
			}
			setSelectedLabOrders([]);
			handleRefreshLabOrders();
			showSuccess(`${selectedLabOrders.length} order(s) deleted successfully`);
		} catch (error) {
			const msg = axios.isAxiosError(error) ? error.response?.data?.error || error.message : String(error);
			showError(`Error deleting order: ${msg}`);
		}
	}, [selectedLabOrders, handleRefreshLabOrders, showSuccess, showError]);

	const handleDeliverResults = async (rowData: LabOrder, analysis?: SampleAnalysis) => {		
		const response = await sendResultsForLabOrder(rowData.id, analysis?.analysis_id);
		if (response) {
			showSuccess("Results delivered successfully.");
			fetchLabOrderData(filterStatus || null);	
		} else {
			showError("Failed to deliver results.");
		}
	};

	const handleCreateSample = async (order: LabOrder, orderItemId: number) => {
		const orderItem = order.order_items?.find((item) => item.id === orderItemId);
		if (!orderItem || !orderItem.sample_id) return;

		try {
			// Parse test_type array for analysis_requested
			const testTypes = parseTestType(orderItem.test_type);

			const newSamp: Sample = {
				...defaultSample,
				sampleID: orderItem.sample_id,
				is_active: true,
				protocol_status: "no_protocols",
				sampleOA: {
					...defaultSample.sampleOA!,
					sampleType: orderItem.sample_type,
					analysis_requested: testTypes, // Now an array
					status: "received",
				},
			};

			await createSample(newSamp);
			showSuccess("Sample created successfully");
			handleRefreshLabOrders();
		} catch (error: any) {
			showError(extractValidationErrors(error).join(' '));
		}
	};

	const handleOpenCreateSampleModal = async (order: LabOrder, orderItemId: number) => {
		const orderItem = order.order_items?.find((item) => item.id === orderItemId);
		if (!orderItem || !orderItem.sample_id) return;

		try {
			// Parse test_type array for analysis_requested
			const testTypes = parseTestType(orderItem.test_type);

			const newSamp: Sample = {
				...defaultSample,
				sampleID: orderItem.sample_id,
				is_active: true,
				protocol_status: "no_protocols",
				sampleOA: {
					...defaultSample.sampleOA!,
					sampleType: orderItem.sample_type,
					analysis_requested: testTypes, // Now an array
					status: "received",
				},
			};
			setNewSample(newSamp);
			setIsCreateSampleModalOpen(true);
		} catch (error: any) {
			showError(extractValidationErrors(error).join(' '));
		}
	};

	return (
		<Layout>
			<PageContainer
				data-testid="orders-root"
			>
				{/* Toolbar: actions + search */}
				<PageToolbar
					primaryAction={{ label: "New Order", icon: <PlusOutlined />, onClick: handleNewLabOrderClick, testId: "orders-create-btn" }}
					extraActions={[
						{
							label: "Edit",
							icon: <EditOutlined />,
							onClick: () => handleEditClick(selectedLabOrders[0]),
							disabled: selectedLabOrders.length !== 1,
						},
						{
							label: selectedLabOrders.length > 1 ? `Delete (${selectedLabOrders.length})` : "Delete",
							icon: <DeleteOutlined />,
							danger: true,
							onClick: handleDeleteOrders,
							disabled: selectedLabOrders.length === 0,
						},
					]}
					filters={[
						{
							type: "search",
							placeholder: "Search by exact order item ID...",
							value: searchQuery,
							onChange: setSearchQuery,
							width: 300,
						},
					]}
				/>

				{/* Inline error */}
				{error && (
					<Alert
						type="error"
						message={error}
						closable
						onClose={() => setError(null)}
					/>
				)}

				{/* Orders Table */}
				<LabOrderTable
					orders={labOrders}
					totalItems={totalLabOrders}
					currentPage={currentPage}
					loading={isLoading}
					setCurrentPage={setCurrentPage}
					selectedOrders={selectedLabOrders}
					setSelectedOrders={setSelectedLabOrders}
					onEditOrder={handleEditClick}
					itemsPerPage={ORDERS_PER_PAGE}
					onPageChange={(page: number) => setCurrentPage(page)}
					handleDeliverResults={handleDeliverResults}
					expandedRows={expandedRows}
					setExpandedRows={setExpandedRows}
					expandedOrderItems={expandedOrderItems}
					setExpandedOrderItems={setExpandedOrderItems}
					onCreateSample={handleCreateSample}
					onOpenSampleModal={handleOpenCreateSampleModal}
					handleFilterStatus={fetchLabOrderData}
				/>

				{/* Order Drawer */}
				{isDrawerOpen && selectedLabOrders.length === 1 && (
					<LabOrderDrawer
						isOpen={isDrawerOpen}
						onClose={handleDrawerClose}
						lab_order_id={selectedLabOrders[0] === 0 ? 0 : selectedLabOrders[0]}
					/>
				)}

				{/* Sample Creation Modal */}
				{isCreateSampleModalOpen && newSample && (
					<SampleModal
						isOpen={isCreateSampleModalOpen}
						onClose={async () => {
							setIsCreateSampleModalOpen(false);
							handleRefreshLabOrders();
						}}
						sample={newSample}
						onFormSubmission={async () => {
							handleRefreshLabOrders();
						}}
						patient={null}
					/>
				)}
			</PageContainer>
		</Layout>
	);
};

export default LabOrdersPage;
